import { TextEditorType, kInternal } from '@liveblocks/core';
import { useRoom, useClient } from '@liveblocks/react';
import { useYjsProvider, getUmbrellaStoreForClient, useReportTextEditor, useCreateTextMention, useDeleteTextMention } from '@liveblocks/react/_private';
import { useInitial } from '@liveblocks/react-ui/_private';
import { getYjsProviderForRoom } from '@liveblocks/yjs';
import { Mark, Extension, getMarkType } from '@tiptap/core';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCursor from '@tiptap/extension-collaboration-cursor';
import { useCallback, useSyncExternalStore, useRef, useEffect } from 'react';
import { AiExtension } from './ai/AiExtension.js';
import { CommentsExtension } from './comments/CommentsExtension.js';
import { MentionExtension } from './mentions/MentionExtension.js';
import { LIVEBLOCKS_COMMENT_MARK_TYPE } from './types.js';

const DEFAULT_OPTIONS = {
  field: "default",
  comments: true,
  mentions: true,
  offlineSupport_experimental: false,
  enablePermanentUserData: false
};
const LiveblocksCollab = Collaboration.extend({
  onCreate() {
    if (!this.editor.extensionManager.extensions.find((e) => e.name === "doc")) {
      console.warn(
        "[Liveblocks] The tiptap document extension is required for Liveblocks collaboration. Please add it or use Tiptap StarterKit extension."
      );
    }
    if (!this.editor.extensionManager.extensions.find(
      (e) => e.name === "paragraph"
    )) {
      console.warn(
        "[Liveblocks] The tiptap paragraph extension is required for Liveblocks collaboration. Please add it or use Tiptap StarterKit extension."
      );
    }
    if (!this.editor.extensionManager.extensions.find((e) => e.name === "text")) {
      console.warn(
        "[Liveblocks] The tiptap text extension is required for Liveblocks collaboration. Please add it or use Tiptap StarterKit extension."
      );
    }
    if (this.editor.extensionManager.extensions.find((e) => e.name === "history")) {
      console.warn(
        "[Liveblocks] The history extension is enabled, Liveblocks extension provides its own. Please remove or disable the History plugin to prevent unwanted conflicts."
      );
    }
  }
});
function useIsEditorReady() {
  const yjsProvider = useYjsProvider();
  const getSnapshot = useCallback(() => {
    const status = yjsProvider?.getStatus();
    return status === "synchronizing" || status === "synchronized";
  }, [yjsProvider]);
  const subscribe = useCallback(
    (callback) => {
      if (yjsProvider === void 0)
        return () => {
        };
      yjsProvider.on("status", callback);
      return () => {
        yjsProvider.off("status", callback);
      };
    },
    [yjsProvider]
  );
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
const YChangeMark = Mark.create({
  name: "ychange",
  inclusive: false,
  parseHTML() {
    return [{ tag: "ychange" }];
  },
  addAttributes() {
    return {
      user: {
        default: null,
        parseHTML: (element) => element.getAttribute("ychange_user") ?? null,
        renderHTML: (attributes) => {
          if (!attributes.user) {
            return {};
          }
          return { "data-ychange-user": attributes.user };
        }
      },
      type: {
        default: null,
        parseHTML: (element) => element.getAttribute("ychange_type") ?? null,
        renderHTML: (attributes) => {
          if (!attributes.type) {
            return {};
          }
          return {
            "data-ychange-type": attributes.type,
            "data-liveblocks": "",
            class: `lb-root lb-tiptap-change lb-tiptap-change-${attributes.type}`
          };
        }
      },
      color: {
        default: null,
        parseHTML: (element) => {
          return element.getAttribute("ychange_color") ?? null;
        },
        renderHTML: () => {
          return {};
        }
      }
    };
  },
  renderHTML({ HTMLAttributes }) {
    return ["ychange", HTMLAttributes, 0];
  }
});
const useLiveblocksExtension = (opts) => {
  const options = {
    ...DEFAULT_OPTIONS,
    ...opts
  };
  const textEditorType = useInitial(
    options.textEditorType ?? TextEditorType.TipTap
  );
  const editor = useRef(null);
  const room = useRoom();
  const isEditorReady = useIsEditorReady();
  const client = useClient();
  const store = getUmbrellaStoreForClient(client);
  const roomId = room.id;
  const yjsProvider = useYjsProvider();
  useEffect(() => {
    if (!isEditorReady || !yjsProvider || !options.initialContent || !editor.current)
      return;
    const ydoc = yjsProvider.getYDoc();
    const hasContentSet = ydoc.getMap("liveblocks_config").get("hasContentSet");
    if (!hasContentSet) {
      ydoc.getMap("liveblocks_config").set("hasContentSet", true);
      editor.current.commands.setContent(options.initialContent);
    }
  }, [isEditorReady, yjsProvider, options.initialContent]);
  useReportTextEditor(textEditorType, options.field ?? DEFAULT_OPTIONS.field);
  const createTextMention = useCreateTextMention();
  const deleteTextMention = useDeleteTextMention();
  return Extension.create({
    name: "liveblocksExtension",
    onCreate() {
      editor.current = this.editor;
      if (this.editor.options.content) {
        console.warn(
          "[Liveblocks] Initial content must be set in the useLiveblocksExtension hook option. Remove content from your editor options."
        );
      }
      if (options.mentions && this.editor.extensionManager.extensions.find(
        (e) => e.name.toLowerCase() === "mention"
      )) {
        console.warn(
          "[Liveblocks] Liveblocks own mention plugin is enabled, using another mention plugin may cause a conflict."
        );
      }
      const self = room.getSelf();
      const updateUser = ({
        info,
        id: userId
      }) => {
        if (!info) {
          return;
        }
        const { user: storedUser } = this.storage.provider.awareness.getLocalState();
        if (this.storage.permanentUserData) {
          const pud = this.storage.permanentUserData.clients.get(
            this.storage.doc.clientID
          );
          if (!pud || pud !== userId) {
            this.storage.permanentUserData.setUserMapping(
              this.storage.doc,
              this.storage.doc.clientID,
              userId ?? "Unknown"
            );
          }
        }
        if (info.name !== storedUser?.name || info.color !== storedUser?.color) {
          this.editor.commands.updateUser({
            name: info.name,
            color: info.color
          });
        }
      };
      if (self?.info) {
        updateUser(self);
      }
      this.storage.unsubs.push(room.events.self.subscribe(updateUser));
      if (options.comments) {
        const commentMarkType = getMarkType(
          LIVEBLOCKS_COMMENT_MARK_TYPE,
          this.editor.schema
        );
        this.storage.unsubs.push(
          store.outputs.threads.subscribe(() => {
            const threadMap = new Map(
              store.outputs.threads.get().findMany(roomId, { resolved: false }, "asc").map((thread) => [thread.id, true])
            );
            function isComment(mark) {
              return mark.type.name === LIVEBLOCKS_COMMENT_MARK_TYPE;
            }
            this.editor.state.doc.descendants((node, pos) => {
              node.marks.forEach((mark) => {
                if (isComment(mark)) {
                  const markThreadId = mark.attrs.threadId;
                  const isOrphan = !threadMap.has(markThreadId);
                  if (isOrphan !== mark.attrs.orphan) {
                    const { tr } = this.editor.state;
                    const trimmedFrom = Math.max(pos, 0);
                    const trimmedTo = Math.min(
                      pos + node.nodeSize,
                      this.editor.state.doc.content.size - 1
                    );
                    tr.removeMark(trimmedFrom, trimmedTo, commentMarkType);
                    tr.addMark(
                      trimmedFrom,
                      trimmedTo,
                      commentMarkType.create({
                        ...mark.attrs,
                        orphan: isOrphan
                      })
                    );
                    this.editor.view.dispatch(tr);
                  }
                }
              });
            });
          })
        );
      }
    },
    onDestroy() {
      this.storage.unsubs.forEach((unsub) => unsub());
    },
    addGlobalAttributes() {
      return [
        {
          types: ["paragraph", "heading"],
          attributes: {
            ychange: { default: null }
          }
        }
      ];
    },
    addStorage() {
      const provider = getYjsProviderForRoom(room, {
        enablePermanentUserData: !!options.ai || options.enablePermanentUserData,
        offlineSupport_experimental: options.offlineSupport_experimental
      });
      return {
        doc: provider.getYDoc(),
        provider,
        permanentUserData: provider.permanentUserData,
        unsubs: []
      };
    },
    addExtensions() {
      const extensions = [
        YChangeMark,
        LiveblocksCollab.configure({
          ySyncOptions: {
            permanentUserData: this.storage.permanentUserData
          },
          document: this.storage.doc,
          field: options.field
        }),
        CollaborationCursor.configure({
          provider: this.storage.provider
        })
      ];
      if (options.comments) {
        extensions.push(CommentsExtension);
      }
      if (options.mentions) {
        extensions.push(
          MentionExtension.configure({
            onCreateMention: createTextMention,
            onDeleteMention: deleteTextMention
          })
        );
      }
      if (options.ai) {
        const resolveContextualPrompt = async ({
          prompt,
          context,
          previous,
          signal
        }) => {
          const result = await room[kInternal].executeContextualPrompt({
            prompt,
            context,
            previous,
            signal
          });
          return JSON.parse(result);
        };
        extensions.push(
          AiExtension.configure({
            resolveContextualPrompt,
            ...typeof options.ai === "boolean" ? {} : options.ai,
            doc: this.storage.doc,
            pud: this.storage.permanentUserData
          })
        );
      }
      return extensions;
    }
  });
};

export { useIsEditorReady, useLiveblocksExtension };
//# sourceMappingURL=LiveblocksExtension.js.map
